import { z } from 'zod';
import { CurrencyCodeSchema, DateOnlySchema, IdSchema, ListQuerySchema, MoneyAmountSchema, PageInfoSchema, TimestampSchema, } from './common.js';
export const ParkKindSchema = z.enum(['park', 'entity']);
export const ParkSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    name: z.string(),
    campspotParkName: z.string().nullable(),
    campspotPropertyCode: z.string().nullable(),
    /**
     * Total units Rent Manager reports for this park, and when that was last
     * confirmed. Null means never synced, which a caller must not treat as zero:
     * "no inventory recorded" and "a park with no units" would render identically
     * and mean opposite things.
     */
    unitCount: z.number().int().nonnegative().nullable(),
    /**
     * Rentable sites, which is not the same as unitCount: Rent Manager holds unit
     * records for storage and seasonal spaces that are not sites. This is the
     * occupancy denominator and the number the client's own reporting uses.
     */
    siteCount: z.number().int().nonnegative().nullable(),
    /** Distinct sites under an open lease. Never exceeds siteCount. */
    occupiedCount: z.number().int().nonnegative().nullable(),
    /** The client's gross move-in target. Their number, not Rent Manager's. */
    moveInGoal: z.number().int().nonnegative().nullable(),
    /** Months the goal covers: 1 monthly, 2 for "1 every other month", 12 for a year. */
    moveInGoalMonths: z.number().int().min(1).max(12),
    /** How the client groups the portfolio. Their classification, not a Rent Manager field. */
    propertyType: z.enum(['RV', 'MH']).nullable(),
    /**
     * 'entity' is Suncoast Communities itself: books in Rent Manager, finances on
     * the portfolio report, but no lots, so no occupancy or reservations.
     */
    kind: ParkKindSchema,
    unitCountSyncedAt: TimestampSchema.nullable(),
    active: z.boolean(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const ParkListResponseSchema = z.strictObject({
    items: z.array(ParkSchema),
    page: PageInfoSchema,
});
/**
 * The owner-to-park mapping (deliverable 1): which parks a park-scoped member
 * -- an owner, investor or property manager -- may see. A member whose roles
 * carry `parks.read.all` sees every park whatever is listed here.
 */
export const ParkAccessSchema = z.strictObject({
    membershipId: IdSchema,
    parkIds: z.array(IdSchema),
});
export const ParkAccessListSchema = z.strictObject({
    items: z.array(ParkAccessSchema),
});
/** Replaces the member's parks with exactly these. Empty removes them all. */
export const SetParkAccessRequestSchema = z.strictObject({
    parkIds: z.array(IdSchema).max(50),
});
/**
 * A Campspot reservation as the API exposes it.
 *
 * Money is a decimal string, never a number: nothing crosses the wire through
 * IEEE 754. Dates are date-only strings, so a stay date cannot acquire a
 * timezone on its way to a chart.
 *
 * Guest identity is deliberately absent. Nothing in the eight contracted
 * deliverables needs a guest's name or email, and shipping them to the browser
 * would put personal data behind nothing but a UI route.
 */
export const ReservationSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    parkId: IdSchema,
    parkName: z.string(),
    confirmation: z.string(),
    /**
     * Which booking system this came from. Hide-A-Way Harbor runs both
     * permanently, so the park alone does not tell you.
     */
    source: z.enum(['campspot', 'wordpress']),
    /**
     * Whether the booking is still live. Every aggregate on this page counts
     * `confirmed` only: a cancelled booking that still added to revenue would be
     * worse than not showing it at all.
     *
     * Campspot rows are always `confirmed` because its Rent Roll only ever emits
     * booked reservations.
     */
    status: z.enum(['confirmed', 'on_hold', 'cancelled']),
    /** Booking date. Campspot calls it Origination Date. */
    originationDate: DateOnlySchema,
    arrivalDate: DateOnlySchema,
    departureDate: DateOnlySchema,
    nights: z.number().int().nonnegative(),
    unitSiteName: z.string(),
    unitSiteType: z.string().nullable(),
    unitSiteClass: z.string().nullable(),
    reservationSource: z.string().nullable(),
    referralSource: z.string().nullable(),
    currency: CurrencyCodeSchema,
    total: MoneyAmountSchema,
    invoiceTotalPayments: MoneyAmountSchema,
    balanceOnReservation: MoneyAmountSchema,
    /** Ratio, not a percentage: 1 means fully paid. */
    percentPaid: z.string(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
/**
 * One park, several, or all of them.
 *
 * Stays the `parkId` key and still accepts a bare id, because a saved view is a
 * stored query string: renaming the key or demanding a list would silently break
 * every view saved before 2026-09-16, and a saved view restoring the wrong thing
 * is the one failure the feature exists to prevent.
 *
 * Absent means every park the caller can see — which RLS decides, not this.
 */
export const ParkIdFilterSchema = z
    .string()
    .transform((value) => value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0))
    .pipe(z.array(IdSchema).min(1).max(25))
    .optional();
export const ReservationListQuerySchema = ListQuerySchema.extend({
    parkId: ParkIdFilterSchema,
    /** Inclusive bounds. Which date they filter depends on dateBasis. */
    from: DateOnlySchema.optional(),
    to: DateOnlySchema.optional(),
    /**
     * Which date the range applies to. 'stay' matches reservations overlapping
     * the window; 'booking' matches reservations created inside it. The two
     * return materially different sets, which is the whole reason deliverable 6
     * needs two heat maps.
     */
    dateBasis: z.enum(['stay', 'booking']).default('stay'),
});
export const ReservationListResponseSchema = z.strictObject({
    items: z.array(ReservationSchema),
    page: PageInfoSchema,
});
// ---------------------------------------------------------------------------
// Deliverable 6 — two heat maps
// ---------------------------------------------------------------------------
export const HeatmapGranularitySchema = z.enum(['day', 'month']);
/**
 * 'booking' buckets a reservation once, on its origination date.
 * 'stay' buckets every night between arrival and departure, so a 180-night
 * stay contributes to roughly six months rather than one.
 */
export const HeatmapBasisSchema = z.enum(['booking', 'stay']);
export const HeatmapCellSchema = z.strictObject({
    parkId: IdSchema,
    parkName: z.string(),
    /** YYYY-MM-DD for day granularity, YYYY-MM for month. */
    bucket: z.string(),
    /** Reservations for the booking basis; occupied site-nights for the stay basis. */
    value: z.number().int().nonnegative(),
    reservations: z.number().int().nonnegative(),
    revenue: MoneyAmountSchema,
});
export const HeatmapQuerySchema = z.strictObject({
    basis: HeatmapBasisSchema,
    granularity: HeatmapGranularitySchema.default('month'),
    from: DateOnlySchema,
    to: DateOnlySchema,
    parkId: ParkIdFilterSchema,
});
export const HeatmapResponseSchema = z.strictObject({
    basis: HeatmapBasisSchema,
    granularity: HeatmapGranularitySchema,
    from: DateOnlySchema,
    to: DateOnlySchema,
    /**
     * What `value` counts, so the UI never has to guess and a screenshot is
     * self-describing.
     */
    valueLabel: z.string(),
    /**
     * Occupancy percentage is deliberately absent: Rent Roll carries only booked
     * reservations, so there is no site-inventory denominator. Populate this once
     * park site counts are available.
     */
    occupancyAvailable: z.literal(false),
    cells: z.array(HeatmapCellSchema),
});
export const ReservationSummarySchema = z.strictObject({
    from: DateOnlySchema,
    to: DateOnlySchema,
    reservations: z.number().int().nonnegative(),
    currency: CurrencyCodeSchema,
    revenue: MoneyAmountSchema,
    collected: MoneyAmountSchema,
    outstanding: MoneyAmountSchema,
    /** Ratio of collected to charged across the window, 0 when nothing charged. */
    percentCollected: z.string(),
    occupiedNights: z.number().int().nonnegative(),
    averageNights: z.number().nonnegative(),
    /** Days between booking and arrival. Ranged 0 to 263 in the first real sample. */
    averageLeadDays: z.number().nonnegative(),
});
/**
 * Occupancy over time, from Rent Manager leases.
 *
 * Kept separate from the Campspot heat map on purpose. That one counts
 * reservations and revenue for short-term stays; this counts long-term sites
 * held, across every park including the two Campspot never sees.
 */
export const OccupancyCellSchema = z.strictObject({
    parkId: IdSchema,
    parkName: z.string(),
    /** YYYY-MM. */
    month: z.string(),
    /** Distinct sites under a lease on the as-of day of that month. */
    occupied: z.number().int().nonnegative(),
    sites: z.number().int().nonnegative(),
    /**
     * Rounded percentage, null when the park has no sites recorded. Deliberately
     * uncapped: the denominator is today's inventory, so a past month can exceed
     * 100 if sites have since been removed. Capping it would turn a real signal
     * into a silent lie, and rejecting it would fail the whole response.
     */
    percent: z.number().int().min(0).nullable(),
    /**
     * Site-nights actually sold in the month, counting long-term leases and
     * confirmed short-term stays together.
     *
     * This is the answer to Stephanie Friedl's question of 2026-09-08: what makes a
     * month "occupied" for someone who is not a full-time resident. Both options
     * she floated - one night counts, or a revenue threshold counts - are
     * thresholds, and every threshold has a cliff: a site sold 14 nights of 30
     * reads identically to one sold 1, or identically to an empty site, depending
     * which side of the line it lands. A rate has no line to argue about. A site
     * sold 3 of 30 nights contributes 10%.
     *
     * It also treats a resident and a three-night guest with the same arithmetic,
     * so it does not need to know which booking system the stay came from - which
     * is what makes it work for Hide-A-Way Harbor's two.
     */
    siteNightsSold: z.number().int().nonnegative(),
    /**
     * Site-nights the park could have sold in the same window.
     *
     * For a past month this is sites times the length of the month. For the CURRENT
     * month it stops at today, otherwise every in-progress month would read as
     * half empty purely because it has not finished yet.
     */
    siteNightsAvailable: z.number().int().nonnegative(),
    /** Rounded rate, null when nothing was available. Uncapped, like `percent`. */
    siteNightPercent: z.number().int().min(0).nullable(),
    /**
     * Leases starting in the month, on a site. A lot swap appears here as a
     * spurious arrival, which is why net is the sturdier number until swap
     * detection exists.
     */
    moveIns: z.number().int().nonnegative(),
    moveOuts: z.number().int().nonnegative(),
    /** moveIns minus moveOuts. A swap nets to zero, so this is unaffected by one. */
    netMoveIns: z.number().int(),
    /**
     * Whether the park has confirmed and closed this month's moves, making moveIns
     * and moveOuts its own count rather than Rent Manager's (Spark 287230). Null
     * while the month is still running.
     */
    movesConfirmed: z.boolean().nullable(),
    /**
     * Short-term revenue earned against this month, apportioned across the months
     * a stay actually covers rather than dumped on its arrival month. A winter
     * stay booked in December and running to March contributes to all four.
     */
    shortTermRevenue: MoneyAmountSchema,
    /**
     * What one lot at this park earns per month under a full-time lease, used as
     * the benchmark a short-term lot is measured against.
     *
     * Derived from the rent actually charged (Rent Manager RC and SRC charge
     * types), because market rents are not in Rent Manager yet. Swap to the real
     * market rent per lot once they are -- the same input Brian's report waits on.
     */
    fullTimeMonthlyRate: MoneyAmountSchema.nullable(),
    /** Rentable sites not held by a full-time resident, so available to short-term. */
    shortTermSites: z.number().int().nonnegative(),
    /**
     * Whether the park sells short stays at all. Lake Letta MHC does not: a vacant
     * lot there is waiting for a resident, not a seasonal lot sitting empty.
     */
    takesShortTerm: z.boolean(),
    /**
     * Short-term lots booked 15 nights or more in the month, Campspot and
     * WordPress together.
     *
     * Stephanie Friedl's definition, 2026-09-24: "any lot that is booked for 15
     * days or more in the month is considered occupied." A threshold, which the
     * site-night rate above deliberately is not -- that one still answers "how
     * full was it really", and this answers the question she reports on.
     *
     * Counted over the whole calendar month even while it is in progress: the test
     * is whether the lot is booked, not whether fifteen nights have passed. Storage
     * units are not lots and never count.
     */
    shortTermOccupied: z.number().int().nonnegative(),
    /**
     * Of the lots no full-time resident holds, the share booked 15+ nights. Her
     * seasonal snapshot: 58 sites at Hideaway, 44 full-time, leaves 14, and 6 of
     * those at 15+ nights is 43%. Null for a park that takes no short-term
     * bookings, where "none of the lots were booked" would be a claim about a
     * season it does not have.
     */
    shortTermPercent: z.number().int().min(0).nullable(),
    /** Full-time plus short-term at 15+ nights: occupancy as the client defines it. */
    totalOccupied: z.number().int().nonnegative(),
    /** Rounded, null when the park has no sites recorded. Uncapped, like `percent`. */
    totalPercent: z.number().int().min(0).nullable(),
    /**
     * Of the park's short-term lots, the share booked 15+ nights: the season
     * panel's own question (2026-09-24), "of the lots with no full-time resident,
     * how many are booked". `shortTermPercent` moved to every site on 2026-10-20 so
     * the grid's two halves add up; this did not, because a season fully booked
     * has to read as full -- Holiday Village's seven seasonal lots all taken is
     * 100%, not 7% of 101 sites. Null for a park with no short-term side.
     */
    seasonPercent: z.number().int().min(0).nullable(),
    /**
     * Stephanie Friedl's measure, 2026-09-17: "give each unit/lot a monthly value
     * equivalent to the full time monthly rate. When the campspot/short term
     * revenue for a month equals that, we call it a month occupied."
     *
     * Economic occupancy rather than physical: 100 means the short-term lots
     * earned exactly what they would have under full-time leases. Deliberately
     * uncapped and expressed as a percentage rather than a yes/no, because a lot
     * earning $640 against a $650 benchmark is not an empty month.
     *
     * Surplus above 100 carries into later months of the same calendar year, which
     * is the "slide that revenue forward" half of her idea -- a strong season is
     * what pays for a quiet one.
     *
     * Null when the park has no rent history to benchmark against.
     */
    revenueOccupancyPercent: z.number().int().min(0).nullable(),
    /**
     * Whether the portal has a short-term record for this month: false before the
     * park's first Campspot or WordPress booking, when short stays were booked in
     * Rent Manager and sit in the full-time figure. `shortTermPercent` is null
     * then, because "no data" is not "nothing booked". True for a park with no
     * short-term side.
     */
    shortTermRecorded: z.boolean(),
    /**
     * Market rent: rent charged in Rent Manager against every site rented at its
     * market rate, "100% rented" on her Summary Rent Roll. Stephanie Friedl,
     * 2026-10-22 (Forge #1113), in place of revenue versus rent, which she did not
     * recognise: "we have some parks where we have people grandfathered in at old
     * prices, and when they sell, we book somebody at the new price."
     *
     * The gap is vacancy loss (empty sites) plus loss to lease (residents below
     * market). Lots filled by Campspot guests count as vacant here: their rent is
     * not in Rent Manager. Null for a month with no rent roll loaded.
     */
    marketRent: MoneyAmountSchema.nullable(),
    rentCharged: MoneyAmountSchema.nullable(),
    vacancyLoss: MoneyAmountSchema.nullable(),
    lossToLease: MoneyAmountSchema.nullable(),
    /** Rent charged over market rent, rounded. */
    marketRentPercent: z.number().int().min(0).nullable(),
    /** The park's target, or null when none is recorded. */
    moveInGoal: z.number().int().nonnegative().nullable(),
    /** Months the target covers; 1 for a monthly goal. */
    moveInGoalMonths: z.number().int().min(1).max(12),
    /**
     * Net move-ins over the park's fiscal year up to and including this month,
     * what its move-in goal had due by then, and the one against the other.
     *
     * Stephanie Friedl, screen recording 2026-10-20, 00:36, over these grids: "we
     * want the standard red, yellow, green ... based off of our year end goals." A
     * month's own net says little against that; the year so far is what the colour
     * judges, the same arithmetic as the portfolio report's move-in bars. Null with
     * no goal, or for a month that has not begun.
     */
    yearNetMoveIns: z.number().int().nullable(),
    yearMoveInsDue: z.number().nullable(),
    yearNetMoveInPacePercent: z.number().int().nullable(),
});
/**
 * How far a month is measured to.
 *
 * `actual` stops at today: a month in progress reports what is true now, and a
 * month that has not started reports today's position, because nothing has
 * happened in it yet. This is the backward-looking view and it is the default.
 *
 * `scheduled` measures every month to its own end, so leases that start or end
 * in the future move the number. Stephanie Friedl asked for this on 2026-09-14 --
 * "a forward button on occupancy by month" -- and it is the same arithmetic, not
 * a forecast: it counts moves already recorded in Rent Manager with a date still
 * to come, and predicts nothing.
 */
export const OccupancyHorizonSchema = z.enum(['actual', 'scheduled']).default('actual');
export const OccupancyQuerySchema = z.strictObject({
    from: DateOnlySchema,
    to: DateOnlySchema,
    parkId: ParkIdFilterSchema,
    horizon: OccupancyHorizonSchema,
});
export const OccupancyResponseSchema = z.strictObject({
    from: DateOnlySchema,
    to: DateOnlySchema,
    /**
     * Rent Manager keeps no history for its IsLicensed flag, so a past month is
     * divided by TODAY's site count. Accurate for recent months and increasingly
     * approximate the further back you look, which is why the view is bounded and
     * the UI says so rather than implying a historical denominator.
     */
    denominatorBasis: z.literal('current-inventory'),
    /** Echoed back so a saved view restores the same picture it was saved from. */
    horizon: z.enum(['actual', 'scheduled']),
    cells: z.array(OccupancyCellSchema),
});
/**
 * A move already recorded against a lease with its date still in the future.
 *
 * Not a forecast and not a prospect: Rent Manager holds one pair of dates per
 * lease, and these are rows where that date has not arrived yet.
 */
export const UpcomingMoveSchema = z.strictObject({
    parkId: IdSchema,
    parkName: z.string(),
    direction: z.enum(['in', 'out']),
    /** YYYY-MM-DD. */
    date: DateOnlySchema,
    /**
     * The site, as Rent Manager labels it.
     *
     * Null for now: the lease sync stores Rent Manager's internal UnitID and not
     * the PropertyUnit label, and showing an internal id beside a park name would
     * read as a site number that is not one. Nullable rather than absent so adding
     * the label later is a sync change and not a contract change.
     *
     * No tenant name, ever. The portal has never carried resident identity and a
     * list of who is leaving is not the place to start.
     */
    site: z.string().nullable(),
});
export const UpcomingMovesQuerySchema = z.strictObject({
    parkId: ParkIdFilterSchema,
    /** How far ahead to look. A year covers the seasonal departures at Lake Letta. */
    through: DateOnlySchema.optional(),
});
export const UpcomingMovesResponseSchema = z.strictObject({
    items: z.array(UpcomingMoveSchema),
});
/**
 * Full-time residents projected forward, park by park, month by month.
 *
 * Stephanie Friedl, screen recording 2026-10-20, 03:58: "the projected move ins
 * and move outs as well. And in that same report you're using, there's just a
 * button that says include anticipated move outs, include expected move ins ...
 * for each park, like easily click over to see November, December, January." Her
 * two switches, in Rent Manager's own words, default on.
 *
 * Like the forward view, this counts moves already on a lease with a date still
 * to come, and predicts nothing: Rent Manager holds one pair of dates per lease,
 * so "anticipated" and "expected" mean recorded and not yet arrived.
 */
const IncludeSchema = z
    .enum(['true', 'false'])
    .default('true')
    .transform((value) => value === 'true');
export const ProjectedMovesQuerySchema = z.strictObject({
    parkId: ParkIdFilterSchema,
    /** How many months to project, this one first. Six reaches the end of peak season. */
    months: z.coerce.number().int().min(1).max(12).default(6),
    includeMoveIns: IncludeSchema,
    includeMoveOuts: IncludeSchema,
});
export const ProjectedMonthSchema = z.strictObject({
    /** YYYY-MM. */
    month: z.string(),
    /** Moves on the books dated after today and within this month. */
    moveIns: z.number().int().nonnegative(),
    moveOuts: z.number().int().nonnegative(),
    /**
     * Full-time residents at the month's end: today's, plus the move-ins and
     * minus the move-outs dated from tomorrow to then, for whichever of the two
     * are switched on.
     */
    occupied: z.number().int(),
    /** Of the park's sites, rounded; null with no sites recorded. */
    percent: z.number().int().nullable(),
});
export const ProjectedParkSchema = z.strictObject({
    parkId: IdSchema,
    parkName: z.string(),
    sites: z.number().int().nonnegative(),
    /** Full-time residents today. */
    occupied: z.number().int().nonnegative(),
    months: z.array(ProjectedMonthSchema),
});
export const ProjectedMovesResponseSchema = z.strictObject({
    asOf: DateOnlySchema,
    includeMoveIns: z.boolean(),
    includeMoveOuts: z.boolean(),
    months: z.array(z.string()),
    parks: z.array(ProjectedParkSchema),
});
